import { useCallback, useEffect, useState } from "react";
import { STORAGE_UI } from "@/constants/storage";

export type ThemeMode = "auto" | "dark" | "light";

const STORAGE_KEY = STORAGE_UI.theme;

function resolveAndApply(mode: ThemeMode) {
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const dark = mode === "dark" || (mode === "auto" && prefersDark);
  document.documentElement.classList.toggle("dark", dark);
  const meta = document.getElementById("meta-theme-color");
  if (meta) meta.setAttribute("content", dark ? "#0f0f0f" : "#F4F1EA");
}

export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "dark" || saved === "light") return saved;
    return "auto";
  });

  useEffect(() => {
    resolveAndApply(mode);
    if (mode !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => resolveAndApply("auto");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    if (next === "auto") {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, next);
    }
    setModeState(next);
  }, []);

  const cycle = useCallback(() => {
    setModeState((prev) => {
      const next: ThemeMode = prev === "auto" ? "dark" : prev === "dark" ? "light" : "auto";
      if (next === "auto") localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  return { mode, setMode, cycle };
}
